import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import Pagination from '../../components/Pagination';
import { ChevronDown, ChevronRight, ChevronUp, MoreVertical, Calendar, X } from 'lucide-react';

const AdminReviewsContent = () => {
  const { isCollapsed } = useSidebar();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [sort, setSort] = useState('newest');
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (statusFilter !== 'all' || dateFilter !== 'all' || sort !== 'newest' || limit !== 20) {
      setCurrentPage(1);
    }
  }, [statusFilter, dateFilter, sort, limit]);

  useEffect(() => {
    fetchReviews();
  }, [statusFilter, currentPage, dateFilter, customStartDate, customEndDate, sort, limit, search]);

  const getDateRange = () => {
    if (dateFilter === 'all') {
      return {};
    }

    const now = new Date();
    let startDate = null;
    let endDate = null;

    switch (dateFilter) {
      case 'today':
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(yesterday);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastWeek':
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        startDate = new Date(lastWeek);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        startDate = new Date(lastMonth);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (customStartDate) {
          startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
        }
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        return {};
    }

    const params = {};
    if (startDate) {
      params.startDate = startDate.toISOString();
    }
    if (endDate) {
      params.endDate = endDate.toISOString();
    }
    return params;
  };

  const fetchReviews = async () => {
    try {
      const dateParams = getDateRange();
      const sortParam = sort === 'oldest' ? 'createdAt:asc' : 'createdAt:desc';
      const params = {
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...dateParams,
        ...(search && { search }),
        page: currentPage,
        limit,
        sort: sortParam
      };
      const response = await api.get('/reviews', { params });
      if (response.data && response.data.reviews) {
        setReviews(response.data.reviews);
        setPagination(response.data.pagination);
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        setReviews(response.data);
        setPagination(null);
      } else {
        setReviews([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/reviews/${id}/status`, { status: newStatus });
      fetchReviews();
      showSuccess(`Review ${newStatus} successfully!`);
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Are you sure you want to delete this review? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/reviews/${id}/admin`);
          fetchReviews();
          showSuccess('Review deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting review');
        }
      }
    );
  };

  const handleBlockUser = async (userId) => {
    showConfirm(
      'Are you sure you want to block this user? They will not be able to login or post reviews.',
      async () => {
        try {
          await api.patch(`/reviews/user/${userId}/block`);
          fetchReviews();
          showSuccess('User blocked successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error blocking user');
        }
      }
    );
  };

  const handleUnblockUser = async (userId) => {
    try {
      await api.patch(`/reviews/user/${userId}/unblock`);
      fetchReviews();
      showSuccess('User unblocked successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error unblocking user');
    }
  };

  return (
    <>
      <SEO title="Manage Reviews - Admin" description="Manage reviews." noindex />

        <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="container-custom py-6 md:py-12">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Reviews</h1>
              </div>

              {/* Search and Filter */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4 flex-wrap">
                <div className="flex flex-1 flex-wrap gap-2 min-w-[200px]">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSearch(searchInput);
                        setCurrentPage(1);
                      }
                    }}
                    placeholder="Search by comment, business, user..."
                    className="flex-1 min-w-[180px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                  <button
                    onClick={() => { setSearch(searchInput); setCurrentPage(1); }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Search
                  </button>
                  {(searchInput || search) && (
                    <button
                      onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setLoading(true);
                  }}
                  className="input-field sm:w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    showDateFilter || dateFilter !== 'all'
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Date Filter</span>
                  {showDateFilter ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  {dateFilter !== 'all' && (
                    <span className="bg-white/20 dark:bg-gray-900/30 px-1.5 py-0.5 rounded text-xs font-semibold">
                      1
                    </span>
                  )}
                </button>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</label>
                    <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Entries per page:</label>
                    <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                      {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Date Filter Panel */}
              {showDateFilter && (
                <div className="card p-4 md:p-6 mb-4 animate-slide-down">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Date Filter
                    </h3>
                    <button
                      onClick={() => setShowDateFilter(false)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Close date filter"
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button
                        onClick={() => {
                          setDateFilter('today');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'today'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('yesterday');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'yesterday'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Yesterday
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('lastWeek');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'lastWeek'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Last Week
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('lastMonth');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'lastMonth'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Last Month
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={() => {
                          setDateFilter('custom');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors mb-3 ${
                          dateFilter === 'custom'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Custom Range
                      </button>
                      {dateFilter === 'custom' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                              className="input-field text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              End Date
                            </label>
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="input-field text-sm w-full"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {dateFilter !== 'all' && (
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => {
                            setDateFilter('all');
                            setCustomStartDate('');
                            setCustomEndDate('');
                          }}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Clear Date Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <PageLoader message="Loading reviews..." />
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Business</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rating</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Comment</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {reviews.map((review, index) => {
                      const isExpanded = expandedRows.has(review._id);
                      const srNo = (currentPage - 1) * limit + index + 1;
                      const toggleExpand = () => {
                        setExpandedRows(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(review._id)) {
                            newSet.delete(review._id);
                          } else {
                            newSet.add(review._id);
                          }
                          return newSet;
                        });
                      };

                      return (
                        <React.Fragment key={review._id}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="font-semibold text-gray-900 dark:text-gray-50">{review.business?.name || 'N/A'}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{review.business?.businessId || ''}</div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="font-semibold text-gray-900 dark:text-gray-50">{review.user?.name || 'N/A'}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{review.user?.email || ''}</div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={`text-base md:text-lg ${
                                      i < review.rating ? 'text-warning-500' : 'text-gray-300 dark:text-gray-600'
                                    }`}
                                  >
                                    ⭐
                                  </span>
                                ))}
                                <span className="ml-2 font-semibold text-gray-900 dark:text-gray-50">{review.rating}</span>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 max-w-md">
                              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{review.comment}</p>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <span
                                className={`badge text-xs ${
                                  review.status === 'approved'
                                    ? 'badge-success'
                                    : review.status === 'pending'
                                    ? 'badge-warning'
                                    : 'badge-error'
                                }`}
                              >
                                {review.status}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <button
                                onClick={toggleExpand}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                                aria-label={isExpanded ? 'Collapse actions' : 'Expand actions'}
                              >
                                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                )}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50 dark:bg-gray-900/50">
                              <td colSpan="7" className="px-4 md:px-6 py-4">
                                <div className="flex flex-wrap gap-3">
                                  {review.status !== 'approved' && (
                                    <button
                                      onClick={() => {
                                        handleStatusChange(review._id, 'approved');
                                        setExpandedRows(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(review._id);
                                          return newSet;
                                        });
                                      }}
                                      className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors text-sm font-medium"
                                    >
                                      Approve
                                    </button>
                                  )}
                                  {review.status !== 'rejected' && (
                                    <button
                                      onClick={() => {
                                        handleStatusChange(review._id, 'rejected');
                                        setExpandedRows(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(review._id);
                                          return newSet;
                                        });
                                      }}
                                      className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium"
                                    >
                                      Reject
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      handleDelete(review._id);
                                      setExpandedRows(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(review._id);
                                        return newSet;
                                      });
                                    }}
                                    className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium"
                                  >
                                    Delete
                                  </button>
                                  {review.user && (
                                    <button
                                      onClick={() => {
                                        handleBlockUser(review.user._id);
                                        setExpandedRows(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(review._id);
                                          return newSet;
                                        });
                                      }}
                                      className="px-4 py-2 bg-warning-600 text-white rounded-lg hover:bg-warning-700 transition-colors text-sm font-medium"
                                    >
                                      Block User
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {reviews.map((review, index) => {
                const isExpanded = expandedRows.has(review._id);
                const srNo = (currentPage - 1) * limit + index + 1;
                const toggleExpand = () => {
                  setExpandedRows(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(review._id)) {
                      newSet.delete(review._id);
                    } else {
                      newSet.add(review._id);
                    }
                    return newSet;
                  });
                };

                return (
                    <div key={review._id} className="card p-4 md:p-6">
                    <div className="mb-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{review.business?.name || 'N/A'}</h3>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{review.user?.name || 'N/A'} • {review.user?.email || ''}</p>
                        </div>
                        <button
                          onClick={toggleExpand}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
                          aria-label={isExpanded ? 'Collapse actions' : 'Expand actions'}
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`text-base ${
                                i < review.rating ? 'text-warning-500' : 'text-gray-300 dark:text-gray-600'
                              }`}
                            >
                              ⭐
                            </span>
                          ))}
                          <span className="ml-2 font-semibold text-gray-900 dark:text-gray-50">{review.rating}</span>
                        </div>
                        <span
                          className={`badge text-xs ${
                            review.status === 'approved'
                              ? 'badge-success'
                              : review.status === 'pending'
                              ? 'badge-warning'
                              : 'badge-error'
                          }`}
                        >
                          {review.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{review.comment}</p>
                    </div>
                    {isExpanded && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        {review.status !== 'approved' && (
                          <button
                            onClick={() => {
                              handleStatusChange(review._id, 'approved');
                              setExpandedRows(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(review._id);
                                return newSet;
                              });
                            }}
                            className="btn-outline text-sm py-1.5 px-3"
                          >
                            Approve
                          </button>
                        )}
                        {review.status !== 'rejected' && (
                          <button
                            onClick={() => {
                              handleStatusChange(review._id, 'rejected');
                              setExpandedRows(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(review._id);
                                return newSet;
                              });
                            }}
                            className="btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                          >
                            Reject
                          </button>
                        )}
                        <button
                          onClick={() => {
                            handleDelete(review._id);
                            setExpandedRows(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(review._id);
                              return newSet;
                            });
                          }}
                          className="btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                        >
                          Delete
                        </button>
                        {review.user && (
                          <button
                            onClick={() => {
                              handleBlockUser(review.user._id);
                              setExpandedRows(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(review._id);
                                return newSet;
                              });
                            }}
                            className="btn-outline text-sm py-1.5 px-3 text-warning-600 dark:text-warning-400 border-warning-600 dark:border-warning-400"
                          >
                            Block User
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {reviews.length === 0 && (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No reviews match your search.' : 'No reviews found.'}</p>
              </div>
            )}
          </>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages >= 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  nextPage={pagination.nextPage}
                  prevPage={pagination.prevPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>
            )}
          </div>
        </main>
    </>
  );
};

const AdminReviews = () => {
  return (
    <AdminLayout>
      <AdminReviewsContent />
    </AdminLayout>
  );
};

export default AdminReviews;

